import { useState, useCallback, useEffect } from "react";
import {
  LogsSettings,
  DEFAULT_LOGS_SETTINGS,
  UseLogsSettingsOptions,
  UseLogsSettingsReturn,
} from "../types";

/**
 * Load settings from localStorage
 */
function loadSettings(storageKey: string): LogsSettings {
  try {
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Merge with defaults to handle missing fields
      return {
        ...DEFAULT_LOGS_SETTINGS,
        ...parsed,
      };
    }
  } catch {
    // Invalid JSON in localStorage, use defaults
  }
  return { ...DEFAULT_LOGS_SETTINGS };
}

/**
 * Save settings to localStorage
 */
function saveSettings(storageKey: string, settings: LogsSettings): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify(settings));
  } catch {
    // localStorage may be full or disabled
  }
}

/**
 * Hook to manage logs display settings with localStorage persistence
 */
export function useLogsSettings(options: UseLogsSettingsOptions): UseLogsSettingsReturn {
  const { storageKey } = options;

  // Initialize state from localStorage
  const [settings, setSettings] = useState<LogsSettings>(() => loadSettings(storageKey));

  // Update settings and persist to localStorage
  const updateSettings = useCallback(
    (updates: Partial<LogsSettings>) => {
      setSettings((prev) => {
        const newSettings = { ...prev, ...updates };
        saveSettings(storageKey, newSettings);
        return newSettings;
      });
    },
    [storageKey]
  );

  // Re-load settings if storageKey changes
  useEffect(() => {
    setSettings(loadSettings(storageKey));
  }, [storageKey]);

  return {
    settings,
    updateSettings,
  };
}
